import React from "react";
import { Link } from "react-router-dom";
import PolicyLayout, { PolicySection } from "./PolicyLayout";

function Privacy() {
  return (
    <PolicyLayout
      title="Privacy Policy"
      intro="This policy explains how mefurvexo handles personal information when you browse this website, place an order, contact us, or otherwise interact with the store."
      contactSubject="privacy or a personal-information request"
    >
      <PolicySection title="1. Who is responsible for your information">
        <p>
          mefurvexo is the data user or controller for personal information it
          decides to collect and use. A payment provider, delivery provider, or
          other independent service may be separately responsible for information
          it collects under its own notice.
        </p>
      </PolicySection>

      <PolicySection title="2. Information we collect">
        <ul>
          <li>Identity and contact details, such as name, email, phone number, billing address, and delivery address.</li>
          <li>Order details, including products, sizes, quantities, price, currency, discounts, delivery status, and returns.</li>
          <li>Payment status and limited transaction references. Complete card details are entered on the payment provider&apos;s hosted page and are not intentionally stored by this website.</li>
          <li>Messages, support requests, reviews, survey answers, and other information you choose to send.</li>
          <li>Technical information such as IP address, browser and device type, page interactions, referral information, and security logs where available.</li>
          <li>Cart contents saved in your browser&apos;s local storage so the cart can persist between visits.</li>
        </ul>
        <p>
          We generally receive this information from you, your browser, our
          payment and delivery partners, and providers that help operate or
          secure the store.
        </p>
      </PolicySection>

      <PolicySection title="3. Why we use information">
        <ul>
          <li>To provide the website, remember a cart, take payment, fulfil an order, and provide returns or warranty support.</li>
          <li>To communicate about an order and respond to enquiries.</li>
          <li>To verify transactions, prevent fraud, protect the website, and enforce store terms.</li>
          <li>To maintain business, tax, accounting, dispute, and compliance records.</li>
          <li>To understand and improve store performance using proportionate technical information.</li>
          <li>To send direct marketing only after the notices and consent required by applicable law, and to record opt-out requests.</li>
        </ul>
        <p>
          Where a law requires a legal basis, processing may be necessary to
          perform a contract, comply with law, pursue a legitimate interest that
          does not override your rights, establish or defend legal claims, or act
          with your consent.
        </p>
      </PolicySection>

      <PolicySection title="4. When providing information is optional">
        <p>
          Providing personal information is generally voluntary. Fields marked
          as required are needed to process the relevant order or request. If
          required details are not provided, we may be unable to accept payment,
          deliver an order, verify a customer, or answer the request. See the{" "}
          <Link to="/data-collection">Personal Information Collection Statement</Link>
          {" "}for the point-of-collection notice.
        </p>
      </PolicySection>

      <PolicySection title="5. Who may receive information">
        <p>We disclose only what is reasonably needed to:</p>
        <ul>
          <li>payment, fraud-screening, hosting, infrastructure, security, and customer-support providers;</li>
          <li>warehousing, fulfilment, postal, courier, customs, and delivery providers;</li>
          <li>professional advisers, insurers, auditors, and transaction counterparties subject to appropriate confidentiality;</li>
          <li>regulators, courts, law enforcement, or other persons where disclosure is required or permitted by law; and</li>
          <li>a successor in a genuine merger, reorganisation, financing, or sale, subject to lawful safeguards.</li>
        </ul>
        <p>
          We do not provide personal information to another business for its own
          direct marketing without the separate notice and consent required by law.
        </p>
      </PolicySection>

      <PolicySection title="6. International processing">
        <p>
          Online-service, payment, and delivery providers may process information
          outside Hong Kong or your home country. Where required, we use
          contractual or other recognised safeguards and disclose only the data
          needed for the service.
        </p>
      </PolicySection>

      <PolicySection title="7. Retention">
        <p>
          We keep personal information only for as long as reasonably needed for
          the purpose collected, including order fulfilment, legal and accounting
          obligations, fraud prevention, warranty support, and disputes. We then
          delete, anonymise, or securely isolate it unless further retention is
          required or permitted by law. Cart data remains on your device until
          cleared by the store logic or your browser settings.
        </p>
      </PolicySection>

      <PolicySection title="8. Security">
        <p>
          We use reasonable organisational and technical measures suited to the
          nature of the information. No internet transmission or storage system is
          completely secure, so absolute security cannot be promised. Please report
          suspected phishing or misuse under our <Link to="/security">Security &amp; Fraud Prevention</Link> policy.
        </p>
      </PolicySection>

      <PolicySection title="9. Direct marketing choices">
        <p>
          Where marketing is offered, we will identify the intended categories of
          personal data and products before seeking any consent required by law.
          Silence is not treated as consent where an explicit indication is
          required. You may opt out without charge by using an unsubscribe control
          or emailing us. Service messages about an order are not marketing.
        </p>
      </PolicySection>

      <PolicySection title="10. Your rights">
        <p>
          Subject to applicable law, you may ask whether we hold your personal
          information and request access, correction, deletion, restriction,
          portability, or an objection to certain uses. You may also withdraw
          consent for future processing. Some requests can be limited where records
          must be retained, another person&apos;s rights are involved, or a legal
          exception applies. We may verify identity before responding and will
          explain any lawful fee in advance.
        </p>
      </PolicySection>

      <PolicySection title="11. Children">
        <p>
          This general-audience store is not designed to collect personal
          information from children without involvement of a parent or guardian.
          If you believe a child provided information without appropriate
          authorisation, contact us so we can review it.
        </p>
      </PolicySection>

      <PolicySection title="12. Cookies, external links, and changes">
        <p>
          See our <Link to="/cookies">Cookie &amp; Local Storage Policy</Link> for
          browser controls. Links to external services are governed by those
          services&apos; privacy notices. We may update this policy when store practices
          or legal requirements change; the revised date will appear above.
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}

export default Privacy;
